export const WIZARD_STAGE = {
  IDLE: "idle",
  ANALYZING: "analyzing",
  ACTIVITY_FLOW: "activity_flow",
  DONE: "done",
}

// One question at a time, in the order shown by the Figma wizard flow.
export const ACTIVITY_FLOW_STEPS = [
  "domain",
  "tier",
  "grades",
  "years",
  "name_description",
  "description",
  "data_collection",
  "indicators",
  "summary",
]

export const GRADE_OPTIONS = ["K", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"]

// This school's actual configured grade levels — the same "School Grade Levels" tags shown on
// the Program Matrix dashboard (ProgramMatrix/Gradelevel.jsx), sourced from
// window.gon.CURRENT_SCHOOL_PROGRAM_MATRIX.selected_grades (set on every program_matrices
// controller action — see ChatBotUI.jsx's areGradesAndPopulationsConfigured, which reads the
// same gon value). Falls back to the universal GRADE_OPTIONS list only if this school hasn't
// configured any grade levels yet, so the picker never ends up with zero options.
export const schoolGradeKeys = () => {
  const selectedGrades = (window.gon
    && window.gon.CURRENT_SCHOOL_PROGRAM_MATRIX
    && window.gon.CURRENT_SCHOOL_PROGRAM_MATRIX.selected_grades) || []
  const keys = selectedGrades.map((grade) => grade.grade).filter(Boolean)
  return keys.length > 0 ? keys : GRADE_OPTIONS
}

// Maps each of this school's configured grade keys to its real student_population (e.g.
// { "3K": 20 }) — the same number ProgramMatrixItemsController#get_max_population_for_grade
// checks server-side, mirrored here so the wizard can cap/flag input before it's submitted.
export const schoolGradePopulations = () => {
  const selectedGrades = (window.gon
    && window.gon.CURRENT_SCHOOL_PROGRAM_MATRIX
    && window.gon.CURRENT_SCHOOL_PROGRAM_MATRIX.selected_grades) || []
  return selectedGrades.reduce((acc, grade) => {
    if (grade.grade) acc[grade.grade] = Number(grade.student_population)
    return acc
  }, {})
}

export const PRIMARY_BTN_STYLE = {
  background: "#2f9e8f",
  borderColor: "#2f9e8f",
  color: "#fff",
  borderRadius: "6px",
  height: "36px",
  padding: "0 16px",
  fontWeight: 600,
}

export const OUTLINE_BTN_STYLE = {
  background: "#fff",
  borderColor: "#d0d0d0",
  color: "#333",
  borderRadius: "6px",
  height: "36px",
  padding: "0 16px",
}

// Explanation text follows "<activity name/other text> <promotes|reinforces> students
// demonstrate awareness of <indicator-specific tail> by <free text>" — "students demonstrate
// awareness of" is the one part of that template that's constant across every indicator (the
// tail after "of" varies per indicator's own language), so the word immediately before it is the
// one actually declaring the connection type. Anchoring on that phrase, not on "names" or on
// "first occurrence of the word anywhere", survives both an activity literally named "promotes"
// and repeated/duplicated text earlier in the string — only the true declaration gets touched.
// Loose text equality/containment for "is this actually the same content" checks — GPT often
// reuses a document value with only trivial polish (capitalization, singular/plural word
// endings, added/dropped trailing punctuation) that a byte-for-byte or plain substring
// comparison would misread as genuinely different text. Deliberately crude (not real stemming
// or fuzzy matching) — it only needs to absorb trivial GPT polish, not general similarity.
export const normalizeWords = (text) => text
  .toLowerCase()
  .replace(/[^a-z0-9\s]/g, "")
  .split(/\s+/)
  .filter(Boolean)
  .map((word) => word.replace(/s$/, ""))
  .join(" ")

export const swapConnectionWord = (text, newType) => {
  if (!text) return text
  const from = newType === "promotes" ? "reinforces" : "promotes"
  return text.replace(
    new RegExp(`\\b${from}\\b(?=\\s+students\\s+demonstrate\\s+awareness\\s+of\\b)`, "i"),
    (word) => (word[0] === word[0].toUpperCase()
      ? `${newType[0].toUpperCase()}${newType.slice(1)}`
      : newType),
  )
}

// Iterative Levenshtein edit distance (single-row DP) — how many single-character insertions,
// deletions, or substitutions turn `a` into `b`. Only used below for the typo-suggestion
// fallback, against a handful of short-to-medium category/tier/length labels at a time, so the
// O(n*m) cost never matters in practice.
const levenshteinDistance = (a, b) => {
  if (a === b) return 0
  if (a.length === 0) return b.length
  if (b.length === 0) return a.length
  let prevRow = Array.from({ length: b.length + 1 }, (_, i) => i)
  for (let i = 1; i <= a.length; i += 1) {
    const currentRow = [i]
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      currentRow[j] = Math.min(
        currentRow[j - 1] + 1, // insertion
        prevRow[j] + 1, // deletion
        prevRow[j - 1] + cost, // substitution
      )
    }
    prevRow = currentRow
  }
  return prevRow[b.length]
}

// Validates a typed free-text answer before the wizard advances to the next field. Enum-backed
// fields (domain/tier/years) must match the school's actual configured list — the same
// case-insensitive comparison ProgramMatrixItem itself uses server-side for category/tier
// (see case_insensitive_category_validation/case_insensitive_tier_validation) — so a typo or an
// out-of-list value (e.g. "abc") is caught here instead of silently advancing to a value the
// backend would reject on save. Free-form fields (name/description/data collection) have no fixed
// vocabulary to check against; blank is the only thing to reject there, as a defensive backstop
// (ChatBotUI's handleSend already refuses to submit blank/whitespace-only input, so this normally
// never fires for those fields, but submitFreeText shouldn't rely solely on that upstream guard).
export const validateFreeTextAnswer = (field, rawText) => {
  const text = (rawText || "").trim()
  if (!text) {
    return { valid: false, error: "This can't be blank — please type a response." }
  }

  const matchAgainstList = (list, label) => {
    const lowerText = text.toLowerCase()
    const exact = list.find((option) => option.toLowerCase() === lowerText)
    if (exact) return { valid: true, value: exact }

    // Falls back to a partial match when the typed answer is a shorter, unambiguous piece of
    // exactly one valid option's full text — e.g. "2-4" for "2-4 years" — rather than requiring
    // the complete option text verbatim. Only accepted when EXACTLY one option contains it:
    // two or more matches (e.g. "supports" fitting both "Social Emotional Supports" and
    // "Behavior Supports") is exactly the case that must still ask the user to be specific,
    // rather than silently guessing which one they meant.
    const partial = list.filter((option) => option.toLowerCase().includes(lowerText))
    if (partial.length === 1) return { valid: true, value: partial[0] }
    if (partial.length > 1) {
      return {
        valid: false,
        error: `"${text}" could mean more than one ${label} (${partial.join(", ")}) — please `
          + "type the full one.",
      }
    }

    // A typo (e.g. "behavior supprt" for "Behavior Supports") shouldn't be flatly rejected the
    // same way a genuinely unrelated value (e.g. "abcd") is — `suggestion` tells the caller to
    // offer it back as a yes/no confirmation instead (see buildTypoConfirmationMessage) rather
    // than forcing a full retype or a hunt for the right button. Threshold is relative to each
    // option's own length (~30% of characters) rather than a fixed distance, since these lists mix
    // short labels ("Universal") and long ones ("Instructional Practices") — a fixed distance
    // would be too lax for the former or too strict for the latter.
    const close = list
      .map((option) => ({ option, distance: levenshteinDistance(lowerText, option.toLowerCase()) }))
      .filter(
        ({ option, distance }) => distance > 0
          && distance <= Math.max(1, Math.ceil(option.length * 0.3)),
      )
      .sort((a, b) => a.distance - b.distance)
    // Only offered when the closest match is unambiguously the best one — a strictly SMALLER
    // distance than the runner-up, not just "also within range". Without that comparison, a typo
    // like "5 years" (distance 1 from "5+ years", but ALSO distance 3 from "2-4 years" — both
    // inside "2-4 years"' own ~30%-of-9-characters threshold of 3) would count as two candidates
    // and fall through to the flat rejection below even though one match is obviously the real
    // one.
    if (close.length > 0) {
      if (close.length === 1 || close[0].distance < close[1].distance) {
        return { valid: false, suggestion: close[0].option }
      }
      // A genuine tie in raw edit distance can still usually be broken cleanly by a number the
      // user actually typed — "2 years" is equidistant (2 character edits either way) from BOTH
      // "2-4 years" and "5+ years", but the "2" it typed only really belongs to one of them.
      // Prefer whichever tied option(s) literally contain that same number; only decide when
      // that narrows it to exactly one, otherwise it's still genuinely ambiguous — same
      // "don't guess" rule the partial-match branch above uses.
      const numericTokens = (str) => str.match(/\d+/g) || []
      const textNumbers = numericTokens(lowerText)
      if (textNumbers.length > 0) {
        const tiedAtMin = close.filter((c) => c.distance === close[0].distance)
        const numberMatches = tiedAtMin.filter(
          (c) => numericTokens(c.option.toLowerCase()).some((n) => textNumbers.includes(n)),
        )
        if (numberMatches.length === 1) {
          return { valid: false, suggestion: numberMatches[0].option }
        }
      }
    }

    return {
      valid: false,
      error: `"${text}" isn't a valid ${label}. Please choose one of: ${list.join(", ")}.`,
    }
  }

  if (field === "domain") {
    return matchAgainstList((window.gon && window.gon.CATEGORIES) || [], "category")
  }
  if (field === "tier") {
    return matchAgainstList(
      (window.gon && window.gon.TIERS_OF_SUPPORT) || ["Universal", "Targeted", "Tertiary"],
      "tier of support",
    )
  }
  if (field === "years") {
    return matchAgainstList(
      (window.gon && window.gon.LENGTHS_OF_TIME) || ["0-1 year", "2-4 years", "5+ years"],
      "length of time",
    )
  }

  return { valid: true, value: text }
}

// Validates a typed grade answer against the ONLY two sources a grade is allowed to come from:
// this activity's own document grades, or this school's configured dashboard grades — a grade
// belonging to neither (e.g. typing "K" when the document said "1, 2" and the school only has
// "PreK" configured) is rejected with both real lists spelled out, rather than silently accepted
// or left to the generic chatbot to improvise a plausible-sounding but fictional confirmation for.
// Matches EVERY known grade token found anywhere in the typed text, not just the first — "1,3",
// "add grade 1, 2 and 3", and "2,3,preK" all resolve as several grades in one go, the same way
// picking multiple buttons in the widget itself would, rather than silently keeping only whichever
// one happens to be checked first and dropping the rest.
export const validateGradeFreeText = (rawText, documentGrades, validGrades) => {
  const text = (rawText || "").trim()
  if (!text) {
    return { valid: false, error: "This can't be blank — please type a grade, or use the buttons above." }
  }

  // Escapes regex metacharacters before dropping a grade token into a \b...\b pattern — tokens
  // are plain alphanumerics in practice (K, 1, PreK, 12), but this keeps the search safe even if
  // a school's configured grade ever contains something like "." or "+".
  const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  // \b stops "1" from matching inside "12" or "21" — a real digit-grade token can only match a
  // typed number when it's a whole, standalone word, never a substring of a longer number.
  const containsToken = (token) => new RegExp(`\\b${escapeRegExp(token)}\\b`, "i").test(text)

  const dashboardGrades = (validGrades || []).filter((g) => containsToken(g))
  // A document grade only counts here when it ISN'T also a dashboard grade — dashboardGrades
  // already covers anything genuinely configured; a document grade that's NOT configured is
  // exactly the out-of-config case the unmatched-grades follow-up otherwise handles.
  const documentOnlyGrades = (documentGrades || []).filter(
    (g) => containsToken(g) && !dashboardGrades.includes(g),
  )

  if (dashboardGrades.length > 0 || documentOnlyGrades.length > 0) {
    return { valid: true, dashboardGrades, documentOnlyGrades }
  }

  const docList = (documentGrades && documentGrades.length > 0) ? documentGrades.join(", ") : "none"
  const dashList = (validGrades && validGrades.length > 0) ? validGrades.join(", ") : "none"
  return {
    valid: false,
    error: `"${text}" isn't a grade in your document (${docList}) or your school's configured `
      + `grade levels (${dashList}). Please add a grade from one of those two lists.`,
  }
}

// Whole-number check for a typed student count (grade population collected via chat for an
// out-of-config grade — see maybeStartPopulationCollection in useProgramMatrixWizard). Pulls the
// number out of a full sentence ("add grade 1 student can be supported 10" -> 10) rather than
// requiring the message to be JUST digits — but still refuses to guess when a message contains
// zero or MORE THAN ONE number, the same failure mode that let the generic GPT chatbot earlier
// "interpret" an ambiguous combined answer ("20,10" — which is which?) instead of just asking
// again. A single embedded number is unambiguous (this step only ever asks for one count at a
// time); two or more numbers in the same message is exactly the case that must still be rejected.
// Negative/decimal tokens are matched and explicitly rejected (with an accurate reason) rather
// than left to \d+ alone, which would silently read "-1" as "1" or split "3.5" into two matches.
export const validatePopulationCount = (rawText, max, gradeToken) => {
  const text = (rawText || "").trim()
  if (!text) {
    return { valid: false, error: "This can't be blank — please enter a whole number (0 or more)." }
  }
  let numberTokens = text.match(/-?\d+(?:\.\d+)?/g) || []
  // A numeric grade (e.g. "1") restated as context — "grade 1 can support 10" — isn't a second,
  // competing count; drop it before checking for ambiguity, but only when there's still at least
  // one OTHER number left afterward, so a message that's genuinely just the grade number itself
  // (no real answer at all) still falls through to the "couldn't find a number" case below rather
  // than silently discarding the user's only token and finding nothing.
  if (gradeToken != null && numberTokens.length > 1) {
    const withoutGrade = numberTokens.filter((t) => t !== String(gradeToken))
    if (withoutGrade.length > 0) numberTokens = withoutGrade
  }
  if (numberTokens.length === 0) {
    return { valid: false, error: "I couldn't find a number in that — please enter a whole number (0 or more)." }
  }
  if (numberTokens.length > 1) {
    return {
      valid: false,
      error: `I found more than one number in that (${numberTokens.join(", ")}) — please answer `
        + "with just this one count.",
    }
  }
  if (!/^\d+$/.test(numberTokens[0])) {
    return { valid: false, error: "Please enter a whole number (0 or more) — no negatives or decimals." }
  }
  const value = parseInt(numberTokens[0], 10)
  if (max != null && value > max) {
    return { valid: false, error: `That can't exceed ${max}.` }
  }
  return { valid: true, value }
}

// Detects which population count a typed answer explicitly names, when it names one at all —
// "20 for being supported" while the "can be supported" question is open means the number is
// meant for the OTHER field, not this one, even though it's the only number in the message and
// would otherwise validate fine as an answer to whatever's currently asked. Requires the fuller
// "being support(ed)"/"can (be) support" phrasing rather than a bare "can"/"being" so it doesn't
// false-trigger on unrelated uses of either common word; returns null when neither is named (the
// ordinary case — answer the field actually asked) or when both are (genuinely ambiguous phrasing
// left to fall through to the ordinary case rather than guessed at).
export const detectPopulationFieldMention = (rawText) => {
  const text = (rawText || "").toLowerCase()
  const mentionsBeing = /being\s+support/.test(text)
  const mentionsCan = /can\s+(be\s+)?support/.test(text)
  if (mentionsBeing && !mentionsCan) return "being"
  if (mentionsCan && !mentionsBeing) return "can"
  return null
}

// window.gon.SEL_STANDARDS is an array of { name, label, standards: { code => { student_indicator_language, adult_indicator_language } } },
// the same per-school CASEL/DESSA catalog shape SelStandardsCatalog#catalog produces server-side.
export const flattenGonSelStandards = () => {
  const catalog = (window.gon && window.gon.SEL_STANDARDS) || []
  const flat = {}
  catalog.forEach((competency) => {
    Object.keys(competency.standards || {}).forEach((code) => {
      flat[code] = {
        category: competency.name,
        label: competency.standards[code].student_indicator_language,
      }
    })
  })
  return flat
}
